import { CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { F } from '@mobily/ts-belt';
import {
  __,
  BirdzTenantService,
  FilterPipe,
  FormatNumberPipe,
  PanelComponent,
  SelectOnEnterWhenUniqueDirective,
  SelectSearchBarComponent,
  SelectWithVirtualScrollDirective,
  Str,
} from '@web/birdz-angular';
import { ContractOption } from 'app/interfaces/contract-option.interface';
import { FilterOptions } from 'app/interfaces/filter-options.interface';
import { Filter } from 'app/interfaces/filter.interface';
import { GeocodeOption } from 'app/interfaces/geocode-option.interface';
import { TechnoOption } from 'app/interfaces/techno-option.interface';
import { FilterService } from 'app/services/filter.service';
import { UserService } from 'app/services/user.service';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, finalize, map, skip, take, tap } from 'rxjs/operators';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    MatTabsModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatSelectModule,
    CdkVirtualScrollViewport,
    CdkVirtualForOf,
    MatOptionModule,
    MatTooltipModule,
    MatButtonModule,
    PanelComponent,
    TranslocoPipe,
    FilterPipe,
    SelectSearchBarComponent,
    SelectWithVirtualScrollDirective,
    FormatNumberPipe,
    SelectOnEnterWhenUniqueDirective,
  ],
})
export class FilterComponent implements OnInit {
  private filterService = inject(FilterService);
  private fb = inject(FormBuilder);
  private tenantService = inject(BirdzTenantService);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  sortByLabel = Str.sortBy('label');
  sortByCityName = Str.sortBy('city_name');
  showContractFilter = false;
  isApplied = true;
  options: FilterOptions = {
    contracts: [],
    geocodes: [],
    technos: [],
  };
  form: FormGroup = this.fb.group({
    contract: null,
    geocodes: [],
    techno: null,
  });
  isLoading = 0;
  selectedTab = 0;
  contractOptionToLabel = (option: ContractOption) => `${option.id} - ${option.label}`;
  geocodeOptionToLabel = (option: GeocodeOption) => option.city_name;
  technoOptionToLabel = (option: TechnoOption) => option.label;
  searchContractFn: (arg0: string, arg1: ContractOption) => boolean = (search, option) =>
    Str.search(search, `${option.id} ${option.label}`);
  searchGeocodeFn: (arg0: string, arg1: GeocodeOption) => boolean = (search, option) =>
    Str.search(search, `${option.id} ${option.city_name}`);

  constructor() {
    this.initIsApplied();
  }

  ngOnInit(): void {
    this.fetchOptions();
    this.resetWhenTenantChanges();
  }

  initIsApplied() {
    combineLatest([this.filterService.filter$, this.form.valueChanges])
      .pipe(
        tap(([_, form]) => {
          const geocodeControl = this.form.controls['geocodes'];
          const technoControl = this.form.controls['techno'];

          // if a contract is selected and the geocode control is disabled, enable it
          if (form.contract && geocodeControl.disabled) {
            geocodeControl.enable();
          }

          // if a contract is selected and the techno control is disabled, enable it
          if (form.contract && technoControl.disabled) {
            technoControl.enable();
          }

          // if no contract is selected and the geocode control is enabled, disable it and empty its value
          if (!form.contract && !geocodeControl.disabled) {
            geocodeControl.disable();
            geocodeControl.setValue(null);
          }

          // if no contract is selected and the techno control is enabled, disable it and empty its value
          if (!form.contract && !technoControl.disabled) {
            technoControl.disable();
            technoControl.setValue(null);
          }
        }),
        map(([filter, form]) => {
          const sortedFilter = {
            contract: filter.contract,
            geocodes: filter.geocodes.slice().sort(),
            techno: filter.techno,
          };
          const sortedForm = {
            contract: form.contract,
            geocodes: form.geocodes?.slice()?.sort(),
            techno: form.techno,
          };
          return F.equals(sortedFilter, sortedForm);
        }),
        distinctUntilChanged(),
        tap((isApplied) => (this.isApplied = isApplied)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  resetWhenTenantChanges() {
    this.tenantService.tenantId$
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.reset();
        })
      )
      .subscribe();
  }

  fetchOptions() {
    this.isLoading++;
    this.filterService
      .fetchOptions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading--),
        tap(() => this.autoSelectContract()),
        tap(() => this.initFromUrl())
      )
      .subscribe();
  }

  setContract(contract: string | null) {
    this.form.patchValue({ contract });
    this.updateOptions();
  }

  patchForm(filter: Filter) {
    this.form.patchValue(filter);
    this.updateOptions();
  }

  autoSelectContract() {
    const options = this.filterService.getOptions();
    // if only one contract, auto select it and submit filter
    if (options.contracts.length === 1) {
      this.setContract(options.contracts[0].id);
      this.submit();
      this.showContractFilter = false;
    } else {
      this.showContractFilter = true;
    }
  }

  initFromUrl() {
    this.route.queryParams
      .pipe(
        take(1),
        tap((params) => {
          if (params['contract']) {
            const filters: Filter = {
              contract: params['contract'],
              geocodes: params['geocodes']?.length > 0 ? params['geocodes'].split(',') : [],
              techno: params['techno'],
            };

            this.filterService.setFilter(filters);
            this.patchForm(filters);
            this.submit();
          } else {
            this.updateOptions();
          }
        })
      )
      .subscribe();
  }

  updateOptions() {
    const wasContractSelected = (id: string) => this.form.get('contract')?.value === id;
    const wasGeocodeSelected = (id: string) => this.form.get('geocodes')?.value?.includes(id);
    const wasTechnoSelected = (id: string) => this.form.get('techno')?.value === id;
    const fullOptions: FilterOptions = this.filterService.getOptions();
    const tenantId = this.userService.getEffectiveTenantId();
    const tenantContractIds = this.userService.getUser()?.getContractIdsHavingTenant(tenantId) ?? [];
    const contractOptionsUnderTenant = fullOptions.contracts.filter((c) => tenantContractIds.includes(c.id));
    const contractIdsUnderTenant = contractOptionsUnderTenant.map((c) => c.id);
    const geocodeOptionsUnderTenant = fullOptions.geocodes.filter((geocode) =>
      geocode.contracts.some((contractId: string) => contractIdsUnderTenant.includes(contractId))
    );
    const options: FilterOptions = {
      contracts: [],
      geocodes: [],
      technos: [],
    };
    const form: Partial<Filter> = {};

    // contracts
    options.contracts = contractOptionsUnderTenant.sort(this.sortByLabel);
    form.contract = options.contracts.map((contract) => contract.id).find(wasContractSelected);

    // geocodes
    const hasSelectedContract = !!form.contract;
    const selectedContractMatchesGeocode = (geocode: GeocodeOption) =>
      form.contract != null && geocode.contracts.includes(form.contract);

    options.geocodes = hasSelectedContract
      ? geocodeOptionsUnderTenant.filter(selectedContractMatchesGeocode).sort(this.sortByCityName)
      : [];
    form.geocodes = options.geocodes.map((geocode) => geocode.id).filter(wasGeocodeSelected);

    const contractTechnos = options.contracts.find((contract) => wasContractSelected(contract.id))?.technos ?? [];
    options.technos = Object.entries(contractTechnos)
      .filter(([_, count]) => count > 0)
      .map(([techno, _]) => ({ id: techno, label: __('TECHNO_' + techno.toUpperCase()) }))
      .sort(this.sortByLabel);

    // automatically select the techno previously selected, or default to watermetering
    // if watermetering is not available, select the first in the list
    const technoIds = options.technos.map((techno) => techno.id);
    form.techno =
      technoIds.find(wasTechnoSelected) ?? technoIds.find((id) => id === 'watermetering') ?? technoIds.at(0);

    // set options and selection
    this.options = options;
    this.form.patchValue(form);
  }

  reset() {
    this.patchForm({ contract: null, geocodes: [], techno: null });
    this.autoSelectContract();
    this.submit();
  }

  submit() {
    const contract: string | null = this.form.get('contract')?.value;
    const geocodes: string[] = this.form.get('geocodes')?.value ?? [];
    const techno: string | null = this.form.get('techno')?.value;

    this.filterService.setFilter({
      contract: contract,
      geocodes: geocodes,
      techno: techno,
    });

    this.router.navigate([], {
      queryParams: {
        contract: contract,
        geocodes: geocodes.length > 0 ? geocodes.join(',') : null,
        techno: techno,
      },
      queryParamsHandling: 'merge',
    });
  }
}
